/* eslint-disable */
import React, { useState } from "react";
import { MDBBtn, MDBCheckbox } from "mdb-react-ui-kit";
import axios from "axios";
import "./ImageProcessingStep.css";

const BASE_API =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

// ============================================================
// ARCHITECTURE:
// editedImages  = PHP _pi images (Step 4 pe auto load)
// processedImages = Bulk processing ke baad
// displayImages = jo bhi latest hai wo dikhao — SIRF EK SECTION
//
// Priority: processedImages > editedImages > uploadedImages
// Individual buttons pe click → editedImages update hoti hai
// Bulk processing → processedImages update hoti hai
// Dono kabhi ek saath nahi dikhenge
// ============================================================

const ImageProcessingStep = ({
  formData,
  processing,
  processingImages,
  progress,
  processedImages, // bulk processing result
  editedImages, // PHP _pi images
  handleSwapImages,
  handleInputChange,
  handleStartImageEditing,
  rotateProcessedImageRight,
  rotateProcessedImageLeft,
  setProcessedImages,
  setEditedImages,
}) => {
  const [selected, setSelected] = useState([]);
  const [perImageLoading, setPerImageLoading] = useState({});

  // ============================================================
  // displayImages — priority: processedImages > editedImages > uploadedImages
  // ============================================================
  const displayImages =
    processedImages?.length > 0
      ? processedImages
      : editedImages?.length > 0
        ? editedImages
        : formData.uploadedImages || [];

  const isBulkProcessed = processedImages?.length > 0;

  // ============================================================
  // Swap
  // ============================================================
  const handleSelect = (index) => {
    let newSelected = [...selected];
    if (newSelected.includes(index)) {
      newSelected = newSelected.filter((i) => i !== index);
    } else {
      if (newSelected.length === 2) newSelected.shift();
      newSelected.push(index);
    }
    setSelected(newSelected);
  };

  const handleSwap = () => {
    if (selected.length !== 2) return;
    const [first, second] = selected;
    handleSwapImages(first, second);
    setSelected([]);
  };

  // ============================================================
  // Per-image: common upload to ERP
  // ============================================================
  const uploadToERP = async (file) => {
    const erpForm = new FormData();
    erpForm.append("images[]", file, file.name);
    const erpRes = await fetch(
      "https://erp.thriftops.com/Upload/appImages.php",
      {
        method: "POST",
        body: erpForm,
      },
    );
    const erpData = await erpRes.json();
    return Array.isArray(erpData)
      ? erpData[0]
      : erpData?.images?.[0] || erpData?.data?.[0] || null;
  };

  // ============================================================
  // Update displayImages at index — always goes to editedImages
  // (never touches processedImages)
  // ============================================================
  const updateDisplayImage = (index, hostedUrl) => {
    if (processedImages?.length > 0) {
      // Bulk processed hain — unhi mein update karo
      setProcessedImages((prev) => {
        const updated = [...prev];
        updated[index] = hostedUrl;
        return updated;
      });
    } else {
      // Edited images mein update karo
      setEditedImages((prev) => {
        const base =
          prev?.length > 0 ? [...prev] : [...(formData.uploadedImages || [])];
        base[index] = hostedUrl;
        return base;
      });
    }
  };

  // ============================================================
  // Per-image: Upscale
  // ============================================================
  const handlePerImageUpscale = async (index, e) => {
    e.stopPropagation();
    const imageUrl = displayImages[index] || formData.uploadedImages[index];
    if (!imageUrl) return;

    setPerImageLoading((prev) => ({ ...prev, [index]: "upscale" }));
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File([blob], `image-${index + 1}.png`, {
        type: blob.type,
      });

      const form = new FormData();
      form.append("image", file);
      const response = await axios.post(
        `${BASE_API}/api/image/ghost-mannequin`,
        form,
      );

      if (response.data?.image) {
        const scaledBlob = await (await fetch(response.data.image)).blob();
        const scaledFile = new File([scaledBlob], `upscale-${index + 1}.png`, {
          type: scaledBlob.type,
        });
        const hostedUrl = await uploadToERP(scaledFile);
        if (hostedUrl) updateDisplayImage(index, hostedUrl);
      }
    } catch (err) {
      console.error(`Upscale failed for image ${index + 1}:`, err);
      alert(`Upscale failed for image ${index + 1}`);
    } finally {
      setPerImageLoading((prev) => ({ ...prev, [index]: null }));
    }
  };

  // ============================================================
  // Per-image: BG Remove
  // ============================================================
  const handlePerImageBgRemove = async (index, e) => {
    e.stopPropagation();
    const imageUrl = displayImages[index] || formData.uploadedImages[index];
    if (!imageUrl) return;

    setPerImageLoading((prev) => ({ ...prev, [index]: "bgremove" }));
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File([blob], `image-${index + 1}.png`, {
        type: blob.type,
      });

      const form = new FormData();
      form.append("image", file);
      const response = await axios.post(
        `${BASE_API}/api/image/remove-background`,
        form,
      );

      if (response.data?.success && response.data?.image) {
        const base64 = response.data.image;
        const arr = base64.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        const processedBlob = new Blob([u8arr], { type: mime });
        const bgFile = new File([processedBlob], `bg-${index + 1}.png`, {
          type: processedBlob.type,
        });
        const hostedUrl = await uploadToERP(bgFile);
        if (hostedUrl) updateDisplayImage(index, hostedUrl);
      }
    } catch (err) {
      console.error(`BG Remove failed for image ${index + 1}:`, err);
      alert(`BG Remove failed for image ${index + 1}`);
    } finally {
      setPerImageLoading((prev) => ({ ...prev, [index]: null }));
    }
  };

  return (
    <div className="step-panel">
      <div className="step-header">
        <h2>Image Processing</h2>
        <p>Process all images at once, or apply individual edits per image</p>
      </div>

      {/* ========== BULK PROCESSING ========== */}
      <div className="bulk-processing-section">
        <h5>Bulk Processing</h5>
        <div className="image-options">
          <MDBCheckbox
            label="Background Removal"
            checked={formData.backgroundRemoval}
            onChange={(e) =>
              handleInputChange("backgroundRemoval", e.target.checked)
            }
          />
          <MDBCheckbox
            label="Upscale Image"
            checked={formData.upscaleImage}
            onChange={(e) =>
              handleInputChange("upscaleImage", e.target.checked)
            }
          />
        </div>
        <MDBBtn
          className="mt-3"
          disabled={
            processing ||
            (!formData.backgroundRemoval && !formData.upscaleImage)
          }
          onClick={handleStartImageEditing}
        >
          {processing ? "Processing..." : "Start Image Processing"}
        </MDBBtn>

        {processingImages && (
          <div className="progress-bar-wrapper mt-3">
            <div className="progress-bar-bg">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span>{progress}% Processing</span>
          </div>
        )}
      </div>

      {/* ========== SINGLE IMAGE GRID — always shows displayImages ========== */}
      {displayImages.length > 0 && (
        <div className="per-image-section mt-4">
          <h5>
            {isBulkProcessed
              ? "Processed Images (Click to select for swap)"
              : "Individual Image Processing"}
          </h5>
          {!isBulkProcessed && (
            <p className="text-muted" style={{ fontSize: "13px" }}>
              Apply upscale or background removal to a specific image
            </p>
          )}

          <div className="image-grid mt-2">
            {displayImages.map((img, index) => {
              const isLoading = perImageLoading[index];
              return (
                <div
                  key={index}
                  // ✅ FIX
                  className={`image-item-individual ${
                    selected.includes(index) ? "selected" : ""
                  }`}
                  onClick={() => handleSelect(index)}
                >
                  {/* Loading overlay */}
                  {isLoading && (
                    <div className="per-image-overlay">
                      <div className="per-image-spinner" />
                      <span>
                        {isLoading === "upscale"
                          ? "Upscaling..."
                          : "Removing BG..."}
                      </span>
                    </div>
                  )}

                  <img
                    src={img}
                    alt={`Item ${index + 1}`}
                    className="image-thumb"
                  />

                  <div className="image-index">{index + 1}</div>

                  {/* Rotate buttons — only in bulk processed mode */}
                  {/* Rotate buttons — HAMESHA */}
                  <div className="rotate-buttons">
                    <button
                      className="rotate-btn-left"
                      onClick={(e) => {
                        e.stopPropagation();
                        rotateProcessedImageLeft(index);
                      }}
                      title="Rotate Left"
                    >
                      ↺
                    </button>
                    <button
                      className="rotate-btn-right"
                      onClick={(e) => {
                        e.stopPropagation();
                        rotateProcessedImageRight(index);
                      }}
                      title="Rotate Right"
                    >
                      ↻
                    </button>
                  </div>

                  {/* Upscale/BG buttons — HAMESHA */}
                  <div className="per-image-actions">
                    <button
                      className="per-img-btn upscale-btn"
                      onClick={(e) => handlePerImageUpscale(index, e)}
                      disabled={!!isLoading}
                      title="Upscale this image"
                    >
                      {isLoading === "upscale" ? "..." : "⬆ Upscale"}
                    </button>
                    <button
                      className="per-img-btn bg-btn"
                      onClick={(e) => handlePerImageBgRemove(index, e)}
                      disabled={!!isLoading}
                      title="Remove background"
                    >
                      {isLoading === "bgremove" ? "..." : "✂ BG Remove"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {selected.length === 2 && (
            <MDBBtn className="mt-3" onClick={handleSwap}>
              Swap Selected Images
            </MDBBtn>
          )}
        </div>
      )}

      <style>{`
        .bulk-processing-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          border: 1px solid #e0e0e0;
        }
        .per-image-section {
          border-top: 1px solid #e0e0e0;
          padding-top: 1rem;
        }
        .image-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        .image-item-individual {
          position: relative;
          background: #f8f9fa;
          border-radius: 8px;
          padding: 0.5rem;
          border: 2px solid transparent;
          transition: all 0.2s ease;
        }
        .image-item-individual:hover {
          border-color: #dc3545;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .image-item-individual.selected {
          border-color: #dc3545;
          box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
          cursor: pointer;
        }
        .image-thumb {
          width: 100%;
          height: 150px;
          object-fit: cover;
          border-radius: 4px;
        }
        .image-index {
          position: absolute;
          top: 8px;
          left: 8px;
          background: rgba(0,0,0,0.6);
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
          z-index: 2;
        }
        .per-image-actions {
          display: flex;
          gap: 6px;
          margin-top: 8px;
        }
        .per-img-btn {
          flex: 1;
          border: none;
          border-radius: 4px;
          padding: 6px 4px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .per-img-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .upscale-btn {
          background: #e8f4fd;
          color: #1a73e8;
          border: 1px solid #1a73e8;
        }
        .upscale-btn:hover:not(:disabled) {
          background: #1a73e8;
          color: white;
        }
        .bg-btn {
          background: #fde8f0;
          color: #dc3545;
          border: 1px solid #dc3545;
        }
        .bg-btn:hover:not(:disabled) {
          background: #dc3545;
          color: white;
        }
        .per-image-overlay {
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0.85);
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: #333;
        }
        .per-image-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #e0e0e0;
          border-top-color: #dc3545;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
       .rotate-buttons {
  position: absolute;
  bottom: 42px;  /* ← upscale/bg buttons ki height se upar */
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}
        .image-item-individual:hover .rotate-buttons {
          opacity: 1;
        }
        .rotate-btn-left,
        .rotate-btn-right {
          background: rgba(0,0,0,0.75);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .rotate-btn-left:hover,
        .rotate-btn-right:hover {
          background: #dc3545;
          transform: scale(1.1);
        }
        .progress-bar-wrapper {
          background: #e0e0e0;
          border-radius: 4px;
          overflow: hidden;
          height: 30px;
          position: relative;
        }
        .progress-bar-bg {
          width: 100%;
          height: 100%;
          background: #f0f0f0;
        }
        .progress-bar-fill {
          background: #dc3545;
          height: 100%;
          transition: width 0.3s ease;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ImageProcessingStep;
